import * as React from 'react'

import { Table, WrenchIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { RouteComponentProps, withRouter } from 'react-router'

import ViewMode from '~/client/src/shared/components/ViewSelector/ViewMode'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import useEquipmentResources from '~/client/src/shared/hooks/useEquipmentResources'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { buildResourceUiConfig } from '~/client/src/shared/resources/builders/buildResourceUiConfig'
import { renderResourceRecordIcon } from '~/client/src/shared/resources/icons/resourceRecordIcon'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'

import { MobileResourceConfig } from '../../components/MobileResource'
import MobileResourceViewLayout from '../../components/MobileResource/MobileResourceViewLayout'
import MobileInitialState from '../../stores/MobileInitialState'
import MobileCommonStore from '../../stores/ui/MobileCommon.store'
import MobileEquipmentEditPanel from './MobileEquipmentEditPanel'

interface IProps {
  state?: MobileInitialState
  common?: MobileCommonStore
}

const EQUIPMENT_UI_DEFAULTS = buildResourceUiConfig(
  ResourceTypeId.OFFLOADING_EQUIPMENT,
)

// The title passes size="lg", a named token Lucide can't resolve.
const HeaderIcon: React.FC<{ className?: string }> = ({ className }) => (
  <WrenchIcon size={28} className={className} />
)

const VIEW_MODES: ViewMode[] = [
  { mode: 'list', Icon: Table, label: Localization.translator.listView },
]

const MobileEquipmentView: React.FC<IProps & RouteComponentProps> = ({
  state,
  common,
}) => {
  const projectId = state?.activeProject?.id
  const runtimeContext = useResourceRuntimeContext()

  const resourceConfig = React.useMemo<MobileResourceConfig>(
    () => ({
      resourceTypeId: ResourceTypeId.OFFLOADING_EQUIPMENT,
      title: Localization.translator.equipment,
      icon: HeaderIcon,
      useRecords: useEquipmentResources,
      initialSortKey: EQUIPMENT_UI_DEFAULTS.defaultSortKey,
      initialSortOrder: EQUIPMENT_UI_DEFAULTS.defaultSortOrder,
      initialGroupByKey: EQUIPMENT_UI_DEFAULTS.defaultGroupingKey,
      countLabel: count => `${count} ${Localization.translator.equipment}`,
      viewModes: VIEW_MODES,
      noneGroupByValue: EQUIPMENT_UI_DEFAULTS.noneGroupingKey,
      emptyMessage: Localization.translator.noDataAvailable,
      resolveLeadingSlot: record =>
        renderResourceRecordIcon(
          ResourceTypeId.OFFLOADING_EQUIPMENT,
          record,
          renderResourceSemanticIcon(
            'equipment',
            'tw:block tw:size-full tw:text-muted-foreground',
          ),
          'tw:block tw:size-full tw:text-muted-foreground',
        ),
      runtimeContext,
    }),
    [runtimeContext],
  )

  React.useEffect(() => {
    common.hideNavBar()
  }, [common])

  return (
    <MobileResourceViewLayout
      config={resourceConfig}
      state={state}
      projectId={projectId}
    >
      <MobileEquipmentEditPanel />
    </MobileResourceViewLayout>
  )
}

export default withRouter(
  inject('state', 'common')(observer(MobileEquipmentView)),
)
